import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
} from '@tanstack/react-table';
import { type MouseEvent as ReactMouseEvent } from 'react';

import { isTargetInteractive } from '../../../utils/is-target-interactive.js';
import { DATA_TABLE_ROW_EXPANDED_ID_SUFFIX } from '../../constants.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import {
  createPrototypeConstructor,
  createTableConstructor,
} from '../types/table.types.js';
import type { DataTableRowActivationEvent } from './row-interactivity-types.js';
import * as styles from './RowInteractivity.sty.js';

/**
 * Minimal shape the hover helpers need, so they do not depend on the full
 * generic table type.
 */
interface HoverStateHolder {
  hoveredRowInfo?: { id: string; element: HTMLElement } | null;
}

function removeHoverStyles(hoverState: HoverStateHolder): void {
  if (hoverState.hoveredRowInfo) {
    hoverState.hoveredRowInfo.element.classList.remove(
      styles.interactiveRow.classNames.variants.hovered.true,
    );
    hoverState.hoveredRowInfo = null;
  }
}

function addHoverStyles(
  hoverState: HoverStateHolder,
  rowId: string,
  row: HTMLElement,
): void {
  row.classList.add(styles.interactiveRow.classNames.variants.hovered.true);
  hoverState.hoveredRowInfo = { id: rowId, element: row };
}

/**
 * Applies hover styles to a row by its ID within the virtualization container.
 */
function applyHoverStylesByRowId(
  hoverState: HoverStateHolder,
  virtualizationContainer: Element | undefined,
  rowId: string,
): void {
  if (!virtualizationContainer) {
    return;
  }
  const rowElement = virtualizationContainer.querySelector<HTMLElement>(
    `[data-rowid="${CSS.escape(rowId)}"]`,
  );
  if (rowElement) {
    addHoverStyles(hoverState, rowId, rowElement);
  }
}

/**
 * When a cell/row actions menu is open, we need to ensure hover styles remain on the row
 * that the menu belongs to.
 * @param table - the table instance
 * @param virtualizationContainer - the virtualization container element
 * @returns `true` if hover styles were maintained, meaning that any other hover logic can be skipped
 */
function maintainOpenMenuRowHover<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  virtualizationContainer: Element | undefined,
): boolean {
  const openMenuRowId = table.getOpenMenuRowId();
  if (openMenuRowId === null) {
    return false;
  }
  if (table.getHoveredRowId() !== openMenuRowId) {
    removeHoverStyles(table);
    applyHoverStylesByRowId(table, virtualizationContainer, openMenuRowId);
  }
  return true;
}

/**
 * Validates the event target and extracts row information.
 */
function getRowInfoFromMouseEvent(event: ReactMouseEvent<HTMLDivElement>): {
  target: HTMLElement | SVGElement;
  row: HTMLElement | null;
  rowId: string | undefined;
  isRowDetailsRow: boolean;
} | null {
  const { target } = event;

  if (!(target instanceof HTMLElement) && !(target instanceof SVGElement)) {
    return null;
  }

  const row: HTMLElement | null = target.closest('[role="row"]');
  const rowId =
    row instanceof HTMLElement && 'rowid' in row.dataset
      ? row.dataset.rowid
      : undefined;
  const isRowDetailsRow =
    rowId?.endsWith(DATA_TABLE_ROW_EXPANDED_ID_SUFFIX) ?? false;

  return { target, row, rowId, isRowDetailsRow };
}

/**
 * Activates a given row for the row interactivity
 * @internal
 */
export function table_setActiveRow<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  updater: Updater<string | null>,
  event?: DataTableRowActivationEvent,
): void {
  table.options.onActiveRowChange?.(updater, event);
}

/**
 * Handles mouse over events for interactive rows.
 * @internal
 */
export function table_handleInteractiveRowMouseOver<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  event: ReactMouseEvent<HTMLDivElement>,
) {
  if (table.options.enableRowInteractivity !== true) {
    return;
  }

  const rowInfo = getRowInfoFromMouseEvent(event);
  if (!rowInfo) {
    return;
  }

  const { target, row, rowId, isRowDetailsRow } = rowInfo;
  const virtualizationContainer = table.getRowVirtualizationContainer();

  // Target is outside the virtualization container's DOM. This covers:
  // 1. Portaled menu content (modal=false): the menu lives in document.body but React
  //    bubbles onMouseOver through the React component tree. Maintain hover on the menu's row.
  // 2. Elements outside the table's React subtree: clear hover.
  if (!virtualizationContainer?.contains(target)) {
    // Keep hover unchanged when the target is the floating menu trigger.
    // The floating menu trigger is portaled to the document.body, but still passed as a target here.
    // This ensures hover styles don't disappear until the next mouse move when opening the floating menu.
    if (
      target instanceof HTMLElement &&
      target.dataset.trigger === 'context-action'
    ) {
      return;
    }
    if (maintainOpenMenuRowHover(table, virtualizationContainer)) {
      return;
    }
    removeHoverStyles(table);
    return;
  }

  // Inside the container but not on a data row (header rows, row details rows, etc.): clear hover.
  // Do not maintain open-menu hover here — only portaled menu content (above) warrants that.
  if (row === null || rowId === undefined || isRowDetailsRow) {
    removeHoverStyles(table);
    return;
  }

  if (table.getHoveredRowId() !== rowId) {
    removeHoverStyles(table);
    addHoverStyles(table, rowId, row);
  }
}

/**
 * Handles mouse leave events for interactive rows.
 * @internal
 */
export function table_handleInteractiveRowMouseLeave<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  event: ReactMouseEvent<HTMLDivElement>,
) {
  if (table.options.enableRowInteractivity !== true) {
    return;
  }

  removeHoverStyles(table);
}

/**
 * Handles click events for interactive rows.
 * @internal
 */
export function table_handleInteractiveRowClick<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  event: ReactMouseEvent<HTMLDivElement>,
) {
  if (table.options.enableRowInteractivity !== true) {
    return;
  }

  const rowInfo = getRowInfoFromMouseEvent(event);
  if (!rowInfo) {
    return;
  }

  const { target, row, rowId, isRowDetailsRow } = rowInfo;
  if (row && rowId && !isRowDetailsRow && !isTargetInteractive(row, target)) {
    event.stopPropagation();
    row.focus();
  }
}

/**
 * Returns a boolean value if the row can be activated.export
 * @internal
 */
function row_getCanActivateRow<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
): boolean {
  return row.table.options.enableRowInteractivity === true;
}

/**
 * Returns a boolean value if the row is currently activated.
 * @internal
 */
export function row_getIsActivated<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
): boolean {
  return row.table.atoms.activeRow?.get() === row.id;
}

/**
 * Activates the row, if it is not activated.
 * Deactivates the row, if it is currently activated.
 * @internal
 */
export function row_toggleActivated<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
  event?: DataTableRowActivationEvent,
): void {
  row.table.setActiveRow(row.getIsActivated() ? null : row.id, event);
}

/**
 * DEPRECATION: [APPDEV-15075] BREAKING-CHANGE: Remove rowInteractivity link
 */
/**
 * Gets the link for the row if the `getLinkForRow` option is provided.
 * @internal
 */
export function row_getLinkForRow<TData extends RowData>(
  row: Row<TDataTableFeatures, TData>,
) {
  return row.table.options.getLinkForRow?.(row);
}

/**
 * Constructs the column row details for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructRowInteractivityFeature(): TableFeature {
  return {
    getInitialState: (initialState) => {
      return {
        activeRow: null,
        ...initialState,
      };
      // This is safe here as the DataTableFeatures configuration, so it is a valid part of
      // TableState<TDataTableFeatures> at runtime — TypeScript just can't prove it
      // through the UnionToIntersection generic constraint.
    },
    assignRowPrototype: createPrototypeConstructor(
      (
        prototype: Record<string, unknown>,
        table: Table<TDataTableFeatures, RowData>,
      ) => {
        assignPrototypeAPIs(
          'DataTableRowInteractivityFeature',
          prototype,
          table,
          {
            row_getCanActivateRow: { fn: (row) => row_getCanActivateRow(row) },
            row_getIsActivated: { fn: (row) => row_getIsActivated(row) },
            row_toggleActivated: {
              fn: (row, event?: DataTableRowActivationEvent) =>
                row_toggleActivated(row, event),
            },
            row_getLinkForRow: { fn: (row) => row_getLinkForRow(row) },
          },
        );
      },
    ),
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('DataTableRowInteractivityFeature', table, {
          table_setActiveRow: {
            fn: (
              updater: Updater<string | null>,
              event?: DataTableRowActivationEvent,
            ) => table_setActiveRow(table, updater, event),
          },
          table_getHoveredRowId: {
            fn: () => table.hoveredRowInfo?.id ?? null,
          },
          table_handleInteractiveRowMouseOver: {
            fn: (event: ReactMouseEvent<HTMLDivElement>) =>
              table_handleInteractiveRowMouseOver(table, event),
          },
          table_handleInteractiveRowMouseLeave: {
            fn: (event: ReactMouseEvent<HTMLDivElement>) =>
              table_handleInteractiveRowMouseLeave(table, event),
          },
          table_handleInteractiveRowClick: {
            fn: (event: ReactMouseEvent<HTMLDivElement>) =>
              table_handleInteractiveRowClick(table, event),
          },
        });
      },
    ),
  };
}

/**
 * Feature implementation for Row Details.
 * @internal
 */
export const DataTableRowInteractivityFeature =
  constructRowInteractivityFeature();
